import { GeneratorOptions, MapType, TextureType } from "../generators";
import { vec2 } from "./generateV2";
export const GeneratorProperties: {
  [key in MapType]: GeneratorOptions;
} = {
  GREEN_HILLS: {
    doErosion: true,
    doFilter: false,
    filterKernelSize: 3,
    numOctaves: 6,
    scale: 350,
    seed: 44,
    selfSimilarity: 1,
    erosionVectors: [
      vec2(0, 0),
      vec2(5.2, 1.3),
      vec2(1.7, 9.2),
      vec2(8.3, 2.8),
    ],
    absoluteNoise: true,
    invertNoise: true,
    erosionScale: 200,
    offsetX: 0,
    offsetY: 0,
    multithreading: true,
    noiseGenerator: "perlin",
  },
  MARS: {
    doErosion: true,
    doFilter: false,
    filterKernelSize: 3,
    numOctaves: 6,
    scale: 1000,
    seed: 50,
    selfSimilarity: 1,
    erosionVectors: [
      vec2(0, 0),
      vec2(5.2, 1.3),
      vec2(1.7, 9.2),
      vec2(8.3, 2.8),
    ],
    absoluteNoise: true,
    invertNoise: true,
    erosionScale: 200,
    offsetX: 0,
    offsetY: 0,
    multithreading: true,
    noiseGenerator: "simplex",
  },
  ICE_LAND: {
    doErosion: true,
    doFilter: false,
    filterKernelSize: 3,
    numOctaves: 6,
    scale: 921,
    seed: 89,
    selfSimilarity: 1,
    erosionVectors: [
      vec2(0, 0),
      vec2(5.2, 1.3),
      vec2(1.7, 9.2),
      vec2(8.3, 2.8),
    ],
    absoluteNoise: false,
    invertNoise: true,
    erosionScale: 200,
    offsetX: -2,
    offsetY: -0.05,
    multithreading: true,
    noiseGenerator: "simplex",
  },
  SAFARI: {
    doErosion: true,
    doFilter: false,
    filterKernelSize: 3,
    numOctaves: 6,
    scale: 350,
    seed: 84,
    selfSimilarity: 1,
    erosionVectors: [
      vec2(0, 0),
      vec2(5.2, 1.3),
      vec2(1.7, 9.2),
      vec2(8.3, 2.8),
    ],
    absoluteNoise: false,
    invertNoise: true,
    erosionScale: 200,
    offsetX: 0.83,
    offsetY: -2,
    multithreading: true,
    noiseGenerator: "perlin",
  },
  MOUNTAINS: {
    doErosion: true,
    doFilter: false,
    filterKernelSize: 3,
    numOctaves: 5,
    scale: 491,
    seed: 1,
    selfSimilarity: 1,
    erosionVectors: [
      {
        x: 84.6,
        y: -13.4,
      },
      {
        x: 5.6,
        y: -100,
      },
      {
        x: 1.7,
        y: 9.2,
      },
      {
        x: 8.3,
        y: 2.8,
      },
    ],
    absoluteNoise: true,
    invertNoise: true,
    erosionScale: 200,
    offsetX: 1.71,
    offsetY: -1.83,
    multithreading: true,
    noiseGenerator: "perlin",
  },
};

export type WaterOptions = {
  show: boolean;
  level: number;
  windForce: number;
  waveHeight: number;
  waveLength: number;
  bumpHeight: number;
  waterColor: [number, number, number];
  colorBlendFactor: number;
  waveCount: number;
};

export type MapOptions = {
  textures: [TextureType, TextureType, TextureType, TextureType];
  levels: [number, number, number];
  minLevel?: number;
  heightScale: number;
  water: WaterOptions;
};

const defaultWaterOptions: WaterOptions = {
  show: true,
  level: 0,
  windForce: -5,
  waveHeight: 0,
  waveLength: 0.5,
  bumpHeight: 0.1,
  waterColor: [0.047, 0.23, 0.17],
  colorBlendFactor: 0.25,
  waveCount: 0,
};

export const MapProperties: {
  [key in MapType]: MapOptions;
} = {
  GREEN_HILLS: {
    levels: [0.46, 0.62, 0.78],
    minLevel: 0.0,
    heightScale: 15,
    textures: [
      {
        name: "sand",
        uv: 2,
      },
      {
        name: "bright_grass",
        uv: 15,
      },
      {
        name: "grass1",
        uv: 19,
      },
      {
        name: "snowy_mountain",
        uv: 5,
      },
    ],

    water: {
      ...defaultWaterOptions,
      level: -4,
    },
  },

  MARS: {
    heightScale: 10,
    levels: [0.5, 0.7, 0.75],
    textures: [
      {
        name: "lava",
        uv: 5,
      },
      {
        name: "red_sand",
        uv: 5,
      },
      {
        name: "red_dirt",
        uv: 5,
      },
      {
        name: "red_mountain",
        uv: 5,
      },
    ],
    water: {
      ...defaultWaterOptions,
      show: false,
      level: -4,
    },
  },
  ICE_LAND: {
    heightScale: 16,
    levels: [0.57, 0.7, 0.86],
    textures: [
      {
        name: "snow",
        uv: 5,
      },
      {
        name: "red_mountain",
        uv: 7,
      },
      {
        name: "mossy_grass",
        uv: 8,
      },
      {
        name: "grass2",
        uv: 5,
      },
    ],
    water: {
      ...defaultWaterOptions,
      level: -4,
    },
  },
  SAFARI: {
    levels: [0.46, 0.62, 0.78],
    minLevel: 0.0,
    heightScale: 15,
    textures: [
      {
        name: "mud_cracked",
        uv: 8,
      },
      {
        name: "sandstone",
        uv: 13,
      },
      {
        name: "red_sand",
        uv: 16,
      },
      {
        name: "grass1",
        uv: 13,
      },
    ],
    water: {
      ...defaultWaterOptions,
      level: -4,
    },
  },
  MOUNTAINS: {
    levels: [0.008, 0.27, 0.64],
    minLevel: 0,
    heightScale: 27,
    textures: [
      {
        name: "sand",
        uv: 2,
      },
      {
        name: "rocky_ground",
        uv: 15,
      },
      {
        name: "smooth_snow",
        uv: 1,
      },
      {
        name: "snowy_mountain",
        uv: 9,
      },
    ],
    water: {
      ...defaultWaterOptions,
      level: -9,
    },
  },
};
